/**
 * @file Public landing page — pages/Landing/components/PublicPageContent.tsx.
 */

import React, { useEffect, useState } from "react";
import {
  publicPageService,
  type PublicPageSection,
} from "../../../services/publicPageService";
import "./PublicPageContent.css";

function escapeHtml(s: string) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function looksLikeHtml(s: string) {
  return /<\/?[a-z][\s\S]*>/i.test(String(s || ""));
}

/** React component — Public Page Content. */
export default function PublicPageContent({
  slug,
  fallbackSections,
  onMeta,
}: {
  slug: string;
  fallbackSections: PublicPageSection[];
  onMeta?: (meta: { title?: string; updatedAt?: string }) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<PublicPageSection[]>(fallbackSections);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const page = await publicPageService.getBySlug(slug);
        if (!mounted) return;

        if (page) {
          onMeta?.({ title: page.title, updatedAt: page.updatedAt });
        }

        if (page?.sections?.length) {
          setSections(page.sections);
          return;
        }

        if (page?.content) {
          const paras = String(page.content)
            .split(/\n{2,}/g)
            .map((p) => p.trim())
            .filter(Boolean);
          if (paras.length) setSections(paras.map((p) => ({ body: p })));
        }
      } catch {
        // Keep fallback content if API isn't available yet.
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [slug, fallbackSections]);

  return (
    <div className="public-page-content">
      {loading && <div className="public-page-loading">Loading…</div>}

      {sections.map((s, idx) => (
        <div key={idx} className="public-page-section">
          {s.heading ? <div className="public-page-heading">{s.heading}</div> : null}
          <div
            className="public-page-body"
            dangerouslySetInnerHTML={{
              __html: looksLikeHtml(s.body)
                ? s.body
                : escapeHtml(s.body).replace(/\n/g, "<br/>"),
            }}
          />
        </div>
      ))}
    </div>
  );
}

